import { ChangeDetectionStrategy, Component, OnDestroy, inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import User from 'entity/User';
import { ReplaySubject, takeUntil } from 'rxjs';
import { socialAppActions, socialAppSelectors, SpecialAccessGroupModalMode } from '../state/app';
import { WebsocketService } from '../services/websocket.service';
import { GroupAccessMessage, OutgoingMessageType, RequestGroupAccessMessage } from 'messages/websocket.messages';
import { SceneService } from '../services/scene.service';
import { Loggable } from 'app/utils/logging/loggable';
import { coreSelectors } from 'app/core/state';
import UserProfile from 'entity/UserProfile';

export const MODAL_ANIMATION_MS = 350;
@Component({
  selector: 'app-modal-special-access',
  templateUrl: './modal-special-access.component.html',
  styleUrl: './modal-special-access.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalSpecialAccessComponent extends Loggable implements OnDestroy {
  private store = inject(Store);
  private websocketService = inject(WebsocketService);
  private sceneService = inject(SceneService);

  private destroyed$ = new ReplaySubject<boolean>(1);
  public readonly specialAccessMode = signal(SpecialAccessGroupModalMode.ALLOW_ACCESS);
  public readonly isModalOpen = signal(false);
  private readonly _user = signal<User | undefined>(undefined);
  private readonly profile = signal<UserProfile | undefined>(undefined);

  protected readonly queue = signal<User[]>([]);

  public constructor() {
    super();
    this.store
      .select(socialAppSelectors.specialAccessGroupModal)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ mode, open }) => {
        this.specialAccessMode.set(mode);
        if (open) {
          this.isModalOpen.set(open);
        } else {
          // display exit animation for the modal
          setTimeout(() => {
            this.isModalOpen.set(open);
          }, MODAL_ANIMATION_MS);
        }
      });

    this.store
      .select(coreSelectors.getUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ user }) => {
        this._user.set(user);
      });

    this.store
      .select(coreSelectors.getUserAndProfileAndAccountInfo)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ userProfile }) => {
        this.profile.set(userProfile);
      });

    if (!this.sceneService.sceneManager) {
      this.error('Scene Manager is not defined');
      return;
    }

    this.sceneService.sceneManager.sagQueue$.pipe(takeUntil(this.destroyed$)).subscribe((sagQueue) => {
      const groupId = this.sceneService.sceneManager?.player?.groupId;
      if (!groupId) {
        return;
      }
      const groupQueue = sagQueue.get(groupId);
      this.queue.set(groupQueue ?? []);
    });
  }

  protected get isUserInGroup() {
    return this.specialAccessMode() === SpecialAccessGroupModalMode.ALLOW_ACCESS;
  }

  protected requestAccess() {
    const msg: RequestGroupAccessMessage = {
      t: OutgoingMessageType.REQUEST_GROUP_ACCESS,
    };

    this.websocketService.sendMessage(msg);
  }

  protected acceptAccessRequest(userId?: string) {
    if (!userId) {
      this.error('userId is not defined');
      return;
    }
    const msg: GroupAccessMessage = {
      t: OutgoingMessageType.GROUP_ACESSS,
      admitted: true,
      userId,
    };
    this.websocketService.sendMessage(msg);

    if (this.queue().length === 1) {
      this.closeModal();
    }
  }

  protected rejectAccessRequest(userId?: string) {
    if (!userId) {
      this.error('userId is not defined');
      return;
    }

    const msg: GroupAccessMessage = {
      t: OutgoingMessageType.GROUP_ACESSS,
      admitted: false,
      userId,
    };
    this.websocketService.sendMessage(msg);

    if (this.queue().length === 1) {
      this.closeModal();
    }
  }

  protected getUsersDisplayName(user: User) {
    return user?.name ? user.name : `${user.firstName ?? ''} ${user.lastName ?? ''}`;
  }

  protected closeModal() {
    this.store.dispatch(socialAppActions.UPDATE_SAG_MODAL({ mode: this.specialAccessMode(), open: false }));
  }

  protected get disableRequestAccessBtn() {
    return !!this.queue().find((participants) => participants.userId === this._user()?.userId);
  }

  protected getTitleCompanyInfo() {
    return this.profile()?.title ?? '';
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
